import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { LANGS } from "@/lib/i18n";

const EMPTY = { room_id: "", arrival: "", departure: "", guests_count: 1, ref_name: "", email: "", phone: "", lang: "it", notes: "" };

export function BookingDialog({ open, onOpenChange, booking, onSaved }) {
  const [rooms, setRooms] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/rooms").then(({ data }) => setRooms(data.filter((r) => r.active)));
    setF(booking ? { ...EMPTY, ...booking } : EMPTY);
  }, [open, booking]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, guests_count: Number(f.guests_count) };
      const { data } = booking ? await api.put(`/bookings/${booking.id}`, payload) : await api.post("/bookings", payload);
      toast.success(booking ? "Prenotazione aggiornata" : "Prenotazione creata");
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader><DialogTitle>{booking ? "Modifica prenotazione" : "Nuova prenotazione"}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-1.5">
            <Label>Camera</Label>
            <Select value={f.room_id || undefined} onValueChange={set("room_id")}>
              <SelectTrigger data-testid="booking-room-select"><SelectValue placeholder="Seleziona camera" /></SelectTrigger>
              <SelectContent>{rooms.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`booking-room-option-${r.id}`}>{r.name} ({r.capacity} posti)</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Arrivo</Label><Input type="date" value={f.arrival} onChange={set("arrival")} data-testid="booking-arrival-input" /></div>
            <div className="space-y-1.5"><Label>Partenza</Label><Input type="date" value={f.departure} onChange={set("departure")} data-testid="booking-departure-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Numero ospiti</Label><Input type="number" min={1} value={f.guests_count} onChange={set("guests_count")} data-testid="booking-guests-input" /></div>
            <div className="space-y-1.5">
              <Label>Lingua ospite</Label>
              <Select value={f.lang} onValueChange={set("lang")}>
                <SelectTrigger data-testid="booking-lang-select"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map(([c, l]) => <SelectItem key={c} value={c}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Referente</Label><Input value={f.ref_name} onChange={set("ref_name")} data-testid="booking-ref-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} data-testid="booking-email-input" /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={f.phone} onChange={set("phone")} data-testid="booking-phone-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} data-testid="booking-save-button">{saving ? "Salvataggio…" : "Salva"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
